import { useMemo } from 'react'
import type { DirectTransferPatient, DirectTransferPrescriber, DirectTransferPrescription } from '@truepill/tpos-types'
import moment from 'moment'
import { PrescriptionOrigin } from 'types'
import type {
  EscriptMedicationPrescribed,
  EscriptPatient,
  EscriptPrescriber,
  Patient,
  Prescriber,
  Prescription,
  RxMedicationData,
  RxPatientData,
  RxPrescriberData,
} from '../types'
import {
  capitalize,
  checkIfProvided,
  formatAddress,
  formatPhoneNumber,
  getPrescriberAddress,
  getPrescriberPhone,
  getPatientAddress,
  labelForDAW,
  getDAW,
  getPatientWeight,
  getCredential,
} from '../utils'

const getRxPatientData = (prescription: Prescription, patient?: Patient): RxPatientData => {
  if (!prescription) return null

  let rxPatient: EscriptPatient | DirectTransferPatient | Patient | undefined
  let phone: string | undefined

  switch (prescription.origin) {
    case PrescriptionOrigin.WRITTEN:
    case PrescriptionOrigin.TELEPHONE:
    case PrescriptionOrigin.FAX: {
      rxPatient = prescription.patient
      phone = formatPhoneNumber(rxPatient?.contacts?.phone)
      break
    }
    case PrescriptionOrigin.ELECTRONIC: {
      rxPatient = prescription.escript.patient
      phone = formatPhoneNumber(rxPatient?.communicationNumbers?.phone?.number)
      break
    }
    case PrescriptionOrigin.PHARMACY: {
      const dtPatient: DirectTransferPatient | undefined = prescription.directTransfer?.patient
      const p: Patient = prescription.patient
      rxPatient = dtPatient ?? p
      phone = formatPhoneNumber(dtPatient?.phone ?? p?.contacts?.phone)
      break
    }
  }

  if (!rxPatient) return null

  return {
    name: checkIfProvided(`${rxPatient.firstName ?? ''} ${rxPatient.lastName ?? ''}`.trim()) as string,
    dob: rxPatient.dob ? moment(rxPatient.dob).format('MM/DD/YYYY') : 'None provided',
    gender: capitalize(checkIfProvided(rxPatient?.gender) as string),
    address: checkIfProvided(formatAddress(getPatientAddress(prescription))) as string,
    phone: checkIfProvided(phone) as string,
    species: checkIfProvided('species' in rxPatient ? rxPatient.species : undefined) as string,
    weight: checkIfProvided(getPatientWeight(patient)) as string,
    guardian: checkIfProvided('guardian' in rxPatient ? rxPatient.guardian : undefined) as string,
  }
}

const getRxPrescriberData = (prescription: Prescription): RxPrescriberData => {
  if (!prescription) return null

  let prescriber: EscriptPrescriber | Prescriber | DirectTransferPrescriber | undefined
  let dea: string | undefined
  let address: string | undefined

  switch (prescription.origin) {
    case PrescriptionOrigin.WRITTEN:
    case PrescriptionOrigin.TELEPHONE:
    case PrescriptionOrigin.FAX: {
      prescriber = prescription.prescriber
      dea = prescriber?.dea
      address = formatAddress(prescriber?.selectedAddress ?? getPrescriberAddress(prescription))
      break
    }
    case PrescriptionOrigin.ELECTRONIC: {
      const p: EscriptPrescriber = prescription.escript?.prescriber
      prescriber = p
      dea = p?.deaNumber
      address = formatAddress(p?.address)
      break
    }
    case PrescriptionOrigin.PHARMACY: {
      const dtPrescriber: DirectTransferPrescriber | undefined = prescription.directTransfer?.prescriber
      const p: Prescriber = prescription.prescriber
      prescriber = dtPrescriber ?? p
      address = formatAddress(dtPrescriber?.address ?? p?.selectedAddress ?? getPrescriberAddress(prescription))
      break
    }
  }

  if (!prescriber) return null

  return {
    name: checkIfProvided(`${prescriber.firstName ?? ''} ${prescriber.lastName ?? ''}`.trim()) as string,
    npi: checkIfProvided(prescriber?.npi) as string,
    address: checkIfProvided(address) as string,
    phone: checkIfProvided(getPrescriberPhone(prescription)) as string,
    dea: checkIfProvided(dea) as string,
    credential: checkIfProvided(getCredential(prescription)) as string,
  }
}

const getRxMedicationData = (prescription: Prescription): RxMedicationData => {
  if (!prescription) return null

  let prescribedRx: Prescription | EscriptMedicationPrescribed | DirectTransferPrescription | undefined
  let medication: string | undefined
  let ndc: string | undefined
  let quantity: string | undefined
  let sig: string | undefined
  let writtenDate: string | undefined
  let expirationDate: string | undefined
  let prescriberNotes: string | undefined
  let numberOfRefills: string | undefined
  let clientNotes

  switch (prescription.origin) {
    case PrescriptionOrigin.WRITTEN:
    case PrescriptionOrigin.TELEPHONE:
    case PrescriptionOrigin.FAX: {
      prescribedRx = prescription
      medication = prescribedRx?.name
      quantity = prescribedRx?.quantity?.toString()
      ndc = prescribedRx?.ndc
      writtenDate = moment(prescribedRx?.writtenDate).format('MM/DD/YYYY')
      expirationDate = moment(prescribedRx?.expirationDate).format('MM/DD/YYYY')
      sig = prescribedRx?.directions
      numberOfRefills = prescribedRx?.numberOfRefills?.toString()
      prescriberNotes = prescribedRx?.note
      break
    }
    case PrescriptionOrigin.ELECTRONIC: {
      prescribedRx = prescription.escript?.medicationPrescribed
      medication = prescribedRx?.drugDescription
      ndc = prescription.escript?.ndc
      quantity = prescribedRx?.quantity?.value.toString()
      writtenDate = moment(prescribedRx?.writtenDate).format('MM/DD/YYYY')
      expirationDate = moment(writtenDate).add(1, 'year').format('MM/DD/YYYY')
      sig = prescribedRx?.sig
      numberOfRefills = prescribedRx?.numberOfRefills?.toString()
      prescriberNotes = prescribedRx?.note
      break
    }
    case PrescriptionOrigin.PHARMACY: {
      const directTransferRx: DirectTransferPrescription | undefined = prescription.directTransfer?.prescription
      const rx: Prescription = prescription
      prescribedRx = directTransferRx ?? rx
      medication = directTransferRx?.medicationName ?? rx?.name
      ndc = directTransferRx?.prescribedNdc ?? rx?.ndc
      quantity = directTransferRx?.quantityWritten ?? rx?.quantity?.toString()
      writtenDate = moment(prescribedRx?.writtenDate).format('MM/DD/YYYY')
      expirationDate = directTransferRx
        ? moment(writtenDate).add(1, 'year').format('MM/DD/YYYY')
        : moment(rx?.expirationDate).format('MM/DD/YYYY')
      sig = directTransferRx?.medicationSig ?? rx?.directions
      numberOfRefills = (directTransferRx?.refillsLeft ?? rx?.numberOfRefills)?.toString()
      prescriberNotes = directTransferRx?.notes ?? rx?.note
      break
    }
  }

  if (!prescribedRx) return null

  return {
    medication,
    ndc,
    ds: prescribedRx?.daysSupply?.toString(),
    daw: labelForDAW(getDAW(prescription)),
    sig,
    writtenDate,
    expirationDate,
    prescriberNotes: checkIfProvided(prescriberNotes) as string,
    clientNotes: checkIfProvided(clientNotes) as string,
    quantity,
    quantityRemaining: prescription.quantityRemaining?.toString(),
    numberOfRefills,
  }
}

export const usePrescribedData = ({ prescription, patient }: { prescription: Prescription; patient?: Patient }) => {
  const rxPatientData = useMemo(() => getRxPatientData(prescription, patient), [prescription, patient])
  const rxPrescriberData = useMemo(() => getRxPrescriberData(prescription), [prescription])
  const rxMedicationData = useMemo(() => getRxMedicationData(prescription), [prescription])

  return { rxPatientData, rxPrescriberData, rxMedicationData }
}
